import { useState, useContext, useEffect } from 'react';
import SplitIO from '@splitsoftware/splitio/types/splitio';
import { splitContext } from '../../providers/Split';
import { Renderings } from './FeatureFlags';

function getCookie(cookieName: string) {
  const name = `${cookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

function isValueValid(value: string) {
  return value === 'off' || value === 'on';
}

function generateExpDate(days: number) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  return `expires=${d.toUTCString()}`;
}

// The implementation of these hooks leverages the web-react split implementation pattern.
function useTreatmentWithConfig<TConfig>(flagName: string): {
  treatment: string;
  config: Partial<TConfig>;
} {
  const [treatment, setTreatment] = useState<string>('control');
  const [config, setConfig] = useState<Partial<TConfig>>({});

  const { client: clientPromise } = useContext(splitContext);

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;
      const { treatment: treatmentValue, config: treatmentConfig } =
        client?.getTreatmentWithConfig(flagName) ?? {};

      setTreatment(treatmentValue ?? 'control');
      setConfig(
        treatmentConfig ? (JSON.parse(treatmentConfig) as TConfig) : {},
      );
    })();
  }, [clientPromise, flagName]);

  return { treatment, config };
}

const useTreatment = (
  flagName: string,
  attributes: SplitIO.Attributes = {},
) => {
  const [treatment, setTreatment] = useState<string | undefined>();

  const { client: clientPromise } = useContext(splitContext);

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;

      setTreatment(client?.getTreatment(flagName, attributes));
    })();
  }, [clientPromise, flagName, attributes]);

  return treatment;
};

const useTreatmentWithAttr = (flagName: string) => {
  const [treatment, setTreatment] = useState<string | undefined>();

  const { client: clientPromise } = useContext(splitContext);

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;
      const attributes: { application_seen?: string } = {};
      const cookieName = `SPLIT_IO_FLAG_${flagName}_TREATMENT`;
      const currentCookieValue = getCookie(cookieName);

      if (currentCookieValue && isValueValid(currentCookieValue)) {
        attributes.application_seen = currentCookieValue;
      }
      const currentTreatment = client?.getTreatment(flagName, attributes);
      setTreatment(currentTreatment);

      if (!currentCookieValue && currentTreatment) {
        const newCookieValue = `${currentTreatment}`;
        const expires = generateExpDate(365);
        document.cookie = `${cookieName}=${newCookieValue};${expires};path=/`;
      }
    })();
  }, [clientPromise, flagName]);

  return treatment;
};
export {
  useTreatment,
  useTreatmentWithConfig,
  useTreatmentWithAttr,
  Renderings,
};
